/**
 * Results of an Obsidian Tasks query block (```tasks) in a page: the matching
 * tasks, live, with the same rows as the task views (check off, open the source).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toIsoDate } from '@nexus/core';
import { parseTaskQuery, runTaskQuery, type Task } from '@nexus/workspace';
import { useTasks } from '../../db/tasks';
import { useCatalog } from '../../state/catalog';
import { TaskRow } from './TaskRow';

export function TaskQueryBlock({ query }: { query: string }) {
  const { t } = useTranslation();
  const tasks = useTasks();
  const byId = useCatalog((s) => s.byId);
  const parsed = useMemo(() => parseTaskQuery(query, toIsoDate(new Date())), [query]);

  const hits = useMemo(() => {
    if (!tasks) return null;
    // Where a task lives: the titles of its page and that page's ancestors.
    const pathOf = (task: Task) => {
      const parts: string[] = [];
      for (let p = byId.get(task.pageId), i = 0; p && i < 50; i++) {
        parts.unshift(p.title);
        p = p.parentId ? byId.get(p.parentId) : undefined;
      }
      return parts.join('/');
    };
    return runTaskQuery(parsed, tasks, pathOf);
  }, [tasks, byId, parsed]);

  const projectTitle = (id: string | null) => (id ? (byId.get(id)?.title ?? null) : null);

  return (
    <div className="nx-task-query" data-testid="task-query" contentEditable={false}>
      {!hits ? null : hits.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('tasks.query.empty')}</p>
      ) : (
        <div>
          {hits.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              projectTitle={projectTitle(task.projectId)}
              compact
            />
          ))}
        </div>
      )}
      <p className="mt-1 text-xs text-fg-muted">
        {hits ? t('tasks.query.count', { count: hits.length }) : null}
        {parsed.unsupported.length > 0 &&
          ` · ${t('tasks.query.unsupported', { lines: parsed.unsupported.join(', ') })}`}
      </p>
    </div>
  );
}
